import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiSave, FiX, FiUpload } from 'react-icons/fi';
import styled from 'styled-components';

import Button from '../../../components/admin/common/Button';
import Modal from '../../../components/admin/common/Modal';
import RichTextEditor from '../../../components/admin/RichTextEditor';
import { 
  getProductById, 
  createProduct, 
  updateProduct,
  uploadProductImages,
  deleteProductImage,
  setMainProductImage
} from '../../../services/productService';
import { getCategories } from '../../../services/categoryService';
import { getTags } from '../../../services/tagService';
import { getSizeCharts } from '../../../services/sizeChartService';

const PriceInput = styled.div`
  position: relative;
  
  input {
    padding-left: 1.5rem;
  }
  
  &::before {
    content: '£';
    position: absolute;
    left: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const ProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [sizeCharts, setSizeCharts] = useState([]);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Form state
  const [product, setProduct] = useState({
    title: '',
    price: '',
    sku: '',
    category: '',
    tags: [],
    sizeChart: '',
    description: {
      details: '',
      sizeFit: '',
      compositionCare: '',
      deliveryReturns: ''
    },
    sizeVariations: [
      { size: 'OneSize', stock: 0, isAvailable: true }
    ],
    images: [],
    isPublished: false
  });

  // Fetch product data for editing
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode) {
        setIsLoading(true);
        try {
          const response = await getProductById(id);
          setProduct(response.data);
          setError(null);
        } catch (err) {
          setError(err.toString());
          console.error('Error fetching product:', err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [id, isEditMode]);

  // Fetch categories and tags
  useEffect(() => {
    const fetchCategoriesAndTags = async () => {
      try {
        const [categoriesResponse, tagsResponse, sizeChartsResponse] = await Promise.all([
          getCategories(),
          getTags(),
          getSizeCharts()
        ]);
        
        setCategories(categoriesResponse.data || []);
        setTags(tagsResponse.data || []);
        setSizeCharts(sizeChartsResponse.success ? sizeChartsResponse.data : []);
        
        // Set default category if creating new product and categories exist
        if (!isEditMode && categoriesResponse.data && categoriesResponse.data.length > 0) {
          setProduct(prev => ({
            ...prev,
            category: categoriesResponse.data[0]._id
          }));
        }
      } catch (err) {
        console.error('Error fetching categories, tags, and size charts:', err);
      }
    };

    fetchCategoriesAndTags();
  }, [isEditMode]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct({ ...product, [name]: value });
  };

  // Handle price input specifically to enforce numeric values
  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setProduct({ ...product, price: value });
    }
  };

  // Handle rich text editor changes
  const handleRichTextChange = (fieldName, content) => {
    setProduct(prev => ({
      ...prev,
      description: {
        ...prev.description,
        [fieldName]: content
      }
    }));
  };

  // Handle size variation updates
  const handleSizeChange = (index, field, value) => {
    const updatedSizeVariations = [...product.sizeVariations];
    
    if (field === 'stock') {
      value = parseInt(value) || 0;
      if (value < 0) value = 0;
    }
    
    updatedSizeVariations[index] = {
      ...updatedSizeVariations[index],
      [field]: field === 'isAvailable' ? value === 'true' : value
    };
    
    setProduct({
      ...product,
      sizeVariations: updatedSizeVariations
    });
  };

  // Handle tag selection
  const handleTagsChange = (e) => {
    // Convert selected options to array
    const selectedTags = Array.from(e.target.selectedOptions, option => option.value);
    setProduct({ ...product, tags: selectedTags });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      let response;
      
      // Create or update product
      if (isEditMode) {
        response = await updateProduct(id, product);
      } else {
        response = await createProduct(product);
      }
      
      // Upload images if files selected
      if (uploadedFiles.length > 0 && response.data) {
        const productId = response.data._id;
        await handleImageUpload(productId);
      }
      
      // Redirect to product list
      navigate('/admin/products');
    } catch (err) {
      setError(err.toString());
      console.error('Error saving product:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    if (e.target.files) {
      setUploadedFiles(Array.from(e.target.files));
    }
  };

  // Handle image upload
  const handleImageUpload = async (productId) => {
    try {
      const formData = new FormData();
      
      uploadedFiles.forEach(file => {
        formData.append('images', file);
      });
      
      // Set as main image if no images exist
      formData.append('isMain', product.images.length === 0);
      
      await uploadProductImages(productId, formData);
    } catch (err) {
      console.error('Error uploading images:', err);
      throw err;
    }
  };

  // Handle image deletion
  const handleDeleteImage = async (imageId) => {
    try {
      await deleteProductImage(id, imageId);
      setProduct({
        ...product,
        images: product.images.filter(img => img._id !== imageId)
      });
    } catch (err) {
      setError(err.toString());
      console.error('Error deleting image:', err);
    }
  };

  // Set main image
  const handleSetMainImage = async (imageId) => {
    try {
      await setMainProductImage(id, imageId);
      
      // Update local state
      setProduct({
        ...product,
        images: product.images.map(img => ({
          ...img,
          isMain: img._id === imageId
        }))
      });
    } catch (err) {
      setError(err.toString());
      console.error('Error setting main image:', err);
    }
  };

  // Preview image
  const handlePreviewImage = (image) => {
    setSelectedImage(image);
    setImagePreviewOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-1/3"></div>
          <div className="h-60 bg-gray-100 rounded w-full"></div>
          <div className="h-40 bg-gray-100 rounded w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">
          {isEditMode ? 'Edit Product' : 'Add New Product'}
        </h1>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/products')}
            icon={<FiX />}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            icon={<FiSave />}
            isLoading={isSaving}
            type="submit"
          >
            {isEditMode ? 'Save Changes' : 'Create Product'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          Error: {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Details */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Basic Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title*
              </label>
              <input
                type="text"
                name="title"
                value={product.title}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU*
              </label>
              <input
                type="text"
                name="sku"
                value={product.sku}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Price</label>
              <PriceInput>
                <input
                  type="text"
                  name="price"
                  value={product.price}
                  onChange={handlePriceChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="0.00"
                />
              </PriceInput>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category*
              </label>
              <select
                name="category"
                value={product.category}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded"
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <select
                multiple
                name="tags"
                value={product.tags}
                onChange={handleTagsChange}
                className="w-full p-2 border border-gray-300 rounded h-24"
              >
                {tags.map((tag) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Hold Ctrl/Cmd key to select multiple tags
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isPublished"
                    value="true"
                    checked={product.isPublished === true}
                    onChange={() => setProduct({ ...product, isPublished: true })}
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-gray-700">Published</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isPublished"
                    value="false"
                    checked={product.isPublished === false}
                    onChange={() => setProduct({ ...product, isPublished: false })}
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-gray-700">Draft</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Size Variations */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Size & Stock</h2>

          {/* Size Chart Selection - shown first so it controls the stock UI below */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Size Chart
            </label>
            <select
              value={
                typeof product.sizeChart === 'object' && product.sizeChart !== null
                  ? product.sizeChart._id
                  : (product.sizeChart || '')
              }
              onChange={(e) => {
                const newSizeChart = e.target.value || null;
                const currentlyHasNoSizeChart = !product.sizeChart || product.sizeChart === '';
                const willHaveNoSizeChart = !newSizeChart;

                let newSizeVariations = product.sizeVariations;
                if (currentlyHasNoSizeChart && !willHaveNoSizeChart) {
                  // Switching from OneSize to sized product
                  newSizeVariations = [
                    { size: 'XS', stock: 0, isAvailable: true },
                    { size: 'S', stock: 0, isAvailable: true },
                    { size: 'M', stock: 0, isAvailable: true },
                    { size: 'L', stock: 0, isAvailable: true },
                    { size: 'XL', stock: 0, isAvailable: true },
                    { size: 'XXL', stock: 0, isAvailable: true }
                  ];
                } else if (!currentlyHasNoSizeChart && willHaveNoSizeChart) {
                  // Switching from sized to OneSize product
                  newSizeVariations = [{ size: 'OneSize', stock: 0, isAvailable: true }];
                }

                setProduct({ ...product, sizeChart: newSizeChart, sizeVariations: newSizeVariations });
              }}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="">-- No Size Chart (One Size) --</option>
              {sizeCharts.map((chart) => (
                <option key={chart._id} value={chart._id}>
                  {chart.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Leave blank for caps, hats, or any one-size product.{' '}
              <Link to="/admin/size-charts/add" target="_blank" className="text-blue-600 hover:underline">
                Create a new size chart
              </Link> if needed.
            </p>
          </div>

          {/* Stock UI: simple field for OneSize, full table for sized products */}
          {(!product.sizeChart || product.sizeChart === '') ? (
            <div>
              <p className="text-sm text-gray-500 mb-3">
                This product has no size chart — it will be sold as <strong>One Size</strong>.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={product.sizeVariations[0]?.stock ?? 0}
                    onChange={(e) => {
                      const stock = parseInt(e.target.value) || 0;
                      setProduct({
                        ...product,
                        sizeVariations: [{ size: 'OneSize', stock, isAvailable: true }]
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 w-1/4">Size</th>
                    <th className="text-left py-2 w-1/4">Stock Quantity</th>
                    <th className="text-left py-2 w-2/4">Available</th>
                  </tr>
                </thead>
                <tbody>
                  {product.sizeVariations.map((variation, index) => (
                    <tr key={variation.size} className="border-b">
                      <td className="py-2">{variation.size}</td>
                      <td className="py-2">
                        <input
                          type="number"
                          min="0"
                          value={variation.stock}
                          onChange={(e) => handleSizeChange(index, 'stock', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                      </td>
                      <td className="py-2">
                        <select
                          value={variation.isAvailable.toString()}
                          onChange={(e) => handleSizeChange(index, 'isAvailable', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded"
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Description Tabs */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Product Description</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                The Details*
              </label>
              <RichTextEditor
                value={product.description?.details || ''}
                onChange={(content) => handleRichTextChange('details', content)}
                placeholder="Enter product details..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size & Fit*
              </label>
              <RichTextEditor
                value={product.description?.sizeFit || ''}
                onChange={(content) => handleRichTextChange('sizeFit', content)}
                placeholder="Enter size and fit information..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Composition & Care*
              </label>
              <RichTextEditor
                value={product.description?.compositionCare || ''}
                onChange={(content) => handleRichTextChange('compositionCare', content)}
                placeholder="Enter composition and care instructions..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery & Returns*
              </label>
              <RichTextEditor
                value={product.description?.deliveryReturns || ''}
                onChange={(content) => handleRichTextChange('deliveryReturns', content)}
                placeholder="Enter delivery and returns information..."
              />
            </div>
          </div>
        </div>

        {/* Image Upload */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Product Images</h2>
          
          {/* Current Images */}
          {isEditMode && product.images && product.images.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Current Images</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {product.images.map((image) => (
                  <div 
                    key={image._id} 
                    className={`relative border rounded group ${image.isMain ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'}`}
                  >
                    <img
                      src={image.url}
                      alt={image.alt || product.title}
                      className="w-full h-40 object-cover cursor-pointer"
                      onClick={() => handlePreviewImage(image)}
                    />
                    <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!image.isMain && (
                        <button
                          type="button"
                          className="bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600"
                          onClick={() => handleSetMainImage(image._id)}
                          title="Set as main image"
                        >
                          <span className="sr-only">Set as main</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        onClick={() => handleDeleteImage(image._id)}
                        title="Delete image"
                      >
                        <span className="sr-only">Delete</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    {image.isMain && (
                      <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs py-1 text-center">
                        Main Image
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Upload New Images */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {isEditMode ? 'Add More Images' : 'Upload Images'}
            </h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center">
              <FiUpload size={24} className="text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                Drag files here or click to browse
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageSelect}
                className="mt-4 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-medium
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-400 mt-2">
                Supported formats: JPG, PNG, GIF. Max file size: 5MB.
              </p>
            </div>
          </div>
          
          {/* Selected Files Preview */}
          {uploadedFiles.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Files</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="border rounded">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Upload preview ${index + 1}`}
                      className="w-full h-40 object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </form>

      {/* Image Preview Modal */}
      <Modal
        isOpen={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        title="Image Preview"
        size="lg"
      >
        {selectedImage && (
          <div className="flex flex-col items-center">
            <img
              src={selectedImage.url}
              alt={selectedImage.alt || product.title}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductFormPage; 